import React, {useEffect, useMemo} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {Link, useLocation, useParams} from "react-router-dom";
import {LoggedInView} from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {
    getCurrentService,
    updateService,
    resetUpdateServiceStatus,
} from "../../store/reducers/ServicesSlice";
import {RequestStatus} from "../../utils/Enums";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogTextarea from "../common/form/ElogTextarea";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {getOrderComponents} from "../../store/reducers/OrderComponents";
import OrderComponent from "../../models/OrderComponent";
import {useFormik, Field, Form, FormikProvider} from "formik";
import {createExtension} from "../../store/reducers/ExtensionsSlice";

interface ModifyServiceScreenProps {
}

export interface ModifyServiceFormProps {
    name: string;
    clientEmail: boolean;
    vendorEmail: boolean;
    clientContactEmail: boolean;
    vendorContactEmail: boolean;
    noAutoInvoice: boolean;
    noValue: boolean;
    nameOnInvoice: boolean;
    railroadAndSea: boolean;
    invoiceOnAdd: boolean;
    mailsOnAdd: boolean;
    orderComponents: {
        [id: number | string]: {
            mandatory: boolean;
            service?: number;
        };
    };
}

export const ModifyServiceScreen: React.FC<ModifyServiceScreenProps> = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const {id} = useParams();

    const servicesState = useAppSelector((state) => state.services);
    const orderComponentsState = useAppSelector((state) => state.orderComponents);

    const requestStatus: RequestStatus = servicesState.updateServiceStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Teenused", url: "/services"},
        {text: "Teenuse muutmine", url: `/services/${id}/modify`},
    ];

    useEffect(() => {
        dispatch(resetUpdateServiceStatus());
    }, [location]);

    useEffect(() => {
        dispatch(getCurrentService({id}));
        dispatch(getOrderComponents());
    }, [id]);

    const selectedOrderComponentsObj = useMemo(() => {
        const obj: { [id: string]: { service: number; mandatory: boolean } } = {};

        (servicesState?.currentService?.order_components || []).forEach((selectedComponent) => {
            const id = selectedComponent.pivot?.order_component_id;
            if (!!id) {
                obj[`${id}a`] = {
                    service: id,
                    mandatory: !!selectedComponent.pivot?.mandatory,
                };
            }
        });

        return obj;
    }, [servicesState?.currentService?.order_components]);

    const renderOrderComponent = (orderComponent: OrderComponent, indentLevel = 0): JSX.Element => (
        <React.Fragment key={orderComponent.id}>
            <tr>
                <td style={{ paddingLeft: `${indentLevel * 25}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    <Field name={`orderComponents[${orderComponent.id.toString()}a].service`}>
                        {({ field }: { field: any }) => (
                            <input
                                type="checkbox"
                                {...field}
                                checked={!!values.orderComponents[`${orderComponent.id.toString()}a`]?.service}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setFieldValue(`orderComponents[${orderComponent.id.toString()}a].service`, isChecked ? orderComponent.id : null);
                                }}
                            />
                        )}
                    </Field>
                </td>
                <td style={{ paddingLeft: `${indentLevel * 40}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    {orderComponent.name}
                </td>
                <td style={{ paddingLeft: `${indentLevel * 40}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    <Field name={`orderComponents[${orderComponent.id.toString()}a].mandatory`} as="select">
                        <option value="true">Jah</option>
                        <option value="false">Ei</option>
                    </Field>
                </td>
            </tr>
            {orderComponent.children &&
            orderComponent.children.map((child) =>
                renderOrderComponent(child, indentLevel + 1)
            )}
        </React.Fragment>
    );

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            name: servicesState?.currentService?.name ?? "",
            account: servicesState?.currentService?.account ?? "",
            clientEmail: !!servicesState?.currentService?.clientEmail,
            vendorEmail: !!servicesState?.currentService?.vendorEmail,
            clientContactEmail: !!servicesState?.currentService?.clientContactEmail,
            vendorContactEmail: !!servicesState?.currentService?.vendorContactEmail,
            noAutoInvoice: !!servicesState?.currentService?.noAutoInvoice,
            noValue: !!servicesState?.currentService?.noValue,
            nameOnInvoice: !!servicesState?.currentService?.nameOnInvoice,
            railroadAndSea: !!servicesState?.currentService?.railroadAndSea,
            isCustoms: !!servicesState?.currentService?.isCustoms,
            isInsurance: !!servicesState?.currentService?.isInsurance,
            invoiceOnLoading: !!servicesState?.currentService?.invoiceOnLoading,
            invoiceOnAdd: !!servicesState?.currentService?.invoiceOnAdd,
            mailsOnAdd: !!servicesState?.currentService?.mailsOnAdd,
            orderComponents: selectedOrderComponentsObj
        },
        onSubmit: (values: any) => {

            // Remove the stupid "a" from the keys in orderComponents
            const modifiedValues: ModifyServiceFormProps = {
                ...values,
                orderComponents: Object.keys(values.orderComponents)
                    .filter(id => {
                        const component = values.orderComponents[id as any];
                        return component && component.service;
                    })
                    .map(id => ({
                        id: parseInt(id),
                        mandatory: values.orderComponents[id as any].mandatory !== undefined ?
                            values.orderComponents[id as any].mandatory :
                            'true',
                    }))
                    .reduce<{ [key: number]: { mandatory: boolean } }>((acc, component) => {
                        acc[component.id] = { mandatory: component.mandatory };
                        return acc;
                    }, {}),
            };

            console.log('ModifyValues',values, modifiedValues);

            dispatch(updateService({id, data: modifiedValues}));

        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Teenuse andmed uuendatud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && servicesState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {servicesState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Teenuse andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="name" mandatory={true}/>
                            {/*<ElogInput label="Kood raamatupidamises" name="account" mandatory={true}/>*/}
                            <ElogCheckbox label="Saada kliendi kiri" name="clientEmail"/>
                            <ElogCheckbox label="Saada hankija kiri" name="vendorEmail"/>
                            <ElogCheckbox label="Saada pealelaadija kontakti kiri" name="clientContactEmail"/>
                            <ElogCheckbox label="Saada mahalaadija kontakti kiri" name="vendorContactEmail"/>
                            <ElogCheckbox label="Arvet ei saadeta automaatselt välja" name="noAutoInvoice"/>
                            <ElogCheckbox label="Alarida ei nõua kauba väärtust" name="noValue"/>
                            <ElogCheckbox label="Arvel kuvatakse teenuse nimi" name="nameOnInvoice"/>
                            <ElogCheckbox label="Mere- ja raudteeveod +- 4 kuud tabelis" name="railroadAndSea"/>
                            <ElogCheckbox label="Toll" name="isCustoms"/>
                            <ElogCheckbox label="Veosekindlustus" name="isInsurance"/>
                            <ElogCheckbox label="Kirjad veo lisamisel" name="mailsOnAdd"/>
                            <ElogCheckbox label="Arve veo lisamisel" name="invoiceOnAdd"/>
                            <ElogCheckbox label="Arve laadimisel" name="invoiceOnLoading"/>
                            </tbody>
                        </table>
                    </div>

                    <div className="order-components-section">
                        <h2>Veo info komponendid</h2>
                        <table className="order-components-table" style={{width: '50%'}}>
                            <thead>
                            <tr>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Kuvatud</th>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Nimi</th>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Kohustuslik</th>
                            </tr>
                            </thead>
                            <tbody>{orderComponentsState.orderComponents.map((orderComponent) => renderOrderComponent(orderComponent))}</tbody>
                        </table>
                    </div>

                    {requestStatus === RequestStatus.Success && (
                        <div className="message msg-ok">
                            <div>
                                <h2>Teenuse andmed uuendatud</h2>
                            </div>
                        </div>
                    )}
                    {requestStatus === RequestStatus.Failed && servicesState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {servicesState.errors.map((error, index) => (
                                    <span key={index}>{error}<br/></span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                        <strong>Uuenda</strong>
                                    </span>
                            </button>
                            <Link to="/services">Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};